/**
 * Displays the list of chats available in a conversation.
 *
 * Ordering: SYSTEM first, then ONE_TO_ONE chats.
 * Each row shows an unread badge when the chat has unread messages.
 *
 * Chat selection is managed by the parent NotificationsPanel; this component
 * is a pure presenter and performs no auto-selection of its own.
 */

import React, {memo, useCallback, useMemo} from "react";
import {Badge, Box, List, ListItemButton, ListItemText, Typography} from "@mui/material";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import {useTranslation} from "react-i18next";
import {NotificationI18N} from "@/translations";
import type {ChatDTO, ConversationDTO} from "./types";
import {ChatType} from "./types";

interface ChatListProps {
    conversation: ConversationDTO | undefined;
    onSelectChat: (chat: ChatDTO) => void;
    selectedChatId?: number;
}

/**
 * Returns a display label for the given chat.
 */
function useChatLabel(chat: ChatDTO, t: ReturnType<typeof useTranslation>["t"]): string {
    if (chat.type === ChatType.SYSTEM) {
        return t(NotificationI18N.chat.list.system);
    }
    // ONE_TO_ONE: prefer backend-resolved display name (partner company), then fall back for robustness
    return chat.displayName ?? chat.companyA?.name ?? chat.companyB?.name ?? t(NotificationI18N.chat.list.oneToOne);
}

/**
 * Stable sort order: SYSTEM = 0, ONE_TO_ONE = 1.
 */
function chatSortOrder(chat: ChatDTO): number {
    if (chat.type === ChatType.SYSTEM) return 0;
    return 1;
}

const ChatList: React.FC<ChatListProps> = ({ conversation, onSelectChat, selectedChatId }) => {
    const { t } = useTranslation();

    const sortedChats = useMemo(() => {
        if (!conversation?.chats) {
            return [];
        }
        return [...conversation.chats].sort((a, b) => chatSortOrder(a) - chatSortOrder(b));
    }, [conversation?.chats]);

    if (sortedChats.length === 0) {
        return (
            <Box sx={{ p: 2, color: "text.secondary" }}>
                <Typography variant="body2">{t(NotificationI18N.chat.list.empty)}</Typography>
            </Box>
        );
    }

    return (
        <List disablePadding sx={{ overflowY: "auto", flex: 1 }}>
            {sortedChats.map((chat) => (
                    <ChatListItem
                            key={chat.id}
                            chat={chat}
                            onSelect={onSelectChat}
                            isSelected={chat.id === selectedChatId}
                    />
            ))}
        </List>
    );
};

interface ChatListItemProps {
    chat: ChatDTO;
    onSelect: (chat: ChatDTO) => void;
    isSelected: boolean;
}

/**
 * A single row in the chat list. Memoized to avoid re-rendering every item when only one
 * chat's selection state or unread count changes.
 */
const ChatListItem: React.FC<ChatListItemProps> = memo(function ChatListItem({
                                                                                 chat,
                                                                                 onSelect,
                                                                                 isSelected
                                                                             }) {
    const { t } = useTranslation();
    const label = useChatLabel(chat, t);
    const unread = chat.unreadCount ?? 0;

    const icon =
            chat.type === ChatType.SYSTEM ? (
                    <NotificationsNoneIcon fontSize="small" color="action"/>
            ) : (
                    <ChatBubbleOutlineIcon fontSize="small" color="action"/>
            );

    const handleClick = useCallback(() => {
        onSelect(chat);
    }, [chat, onSelect]);

    return (
        <ListItemButton
                onClick={handleClick}
            selected={isSelected}
            sx={{ borderBottom: "1px solid", borderColor: "divider", py: 1.5 }}
        >
            <Box sx={{ mr: 1.5, display: "flex", alignItems: "center" }}>{icon}</Box>
            <ListItemText
                primary={
                    <Typography variant="body2" fontWeight={unread > 0 ? "bold" : "normal"}>
                        {label}
                    </Typography>
                }
            />
            {unread > 0 && <Badge badgeContent={unread} color="error" sx={{ml: 1}}/>}
        </ListItemButton>
    );
});

export default ChatList;
